import { useRef, type KeyboardEvent } from 'react';

export interface ChoiceChipOption<T extends string> {
  value: T;
  label: string;
}

export interface ChoiceChipsProps<T extends string> {
  /** Names the group for screen readers ("Meal for Sarah"). */
  label: string;
  options: ChoiceChipOption<T>[];
  value: T | null | undefined;
  onChange: (value: T) => void;
  /** The invitation's accent colour for the chosen chip. */
  accent?: string;
  disabled?: boolean;
  id?: string;
}

/**
 * One choice from a few, as large chips a thumb can hit — the guest-facing
 * pages' answer to a `<select>`, which on a phone is a small target that opens
 * a system picker away from the question.
 */
export function ChoiceChips<T extends string>({
  label,
  options,
  value,
  onChange,
  accent,
  disabled,
  id,
}: ChoiceChipsProps<T>) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const chosenIndex = options.findIndex((option) => option.value === value);
  // One tab stop for the group, as a radio group has: the chosen chip, else
  // the first; the arrow keys move the choice, Home and End to either end.
  const focusIndex = chosenIndex >= 0 ? chosenIndex : 0;
  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const last = options.length - 1;
    const next =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? index === last
          ? 0
          : index + 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? index === 0
            ? last
            : index - 1
          : event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? last
              : null;
    if (next === null) return;
    event.preventDefault();
    onChange(options[next].value);
    refs.current[next]?.focus();
  };
  return (
    <div id={id} role="radiogroup" aria-label={label} className="flex flex-wrap gap-2">
      {options.map((option, index) => {
        const chosen = value === option.value;
        return (
          <button
            key={option.value}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="radio"
            aria-checked={chosen}
            tabIndex={index === focusIndex ? 0 : -1}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            className={`min-h-11 px-4 rounded-full border text-base sm:text-sm font-medium transition-colors disabled:opacity-60 ${
              chosen ? '' : 'border-gray-300 text-gray-700 hover:border-gray-400'
            }`}
            style={
              chosen
                ? {
                    borderColor: accent ?? undefined,
                    color: accent ?? undefined,
                    backgroundColor: accent ? `${accent}14` : undefined,
                  }
                : undefined
            }
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
